import { roundNoFixed } from 'components/utils';
import { unit } from 'mathjs';

export const processTankData = async (tankData, index, lastOps, tankDataMap, volumeUnit, groupStartRow, cellarsRef, config = {}) => {
  const operation = tankData.tankId ? lastOps.find(op => op.tankNumber === tankData.tankId) : null;

  const { compartment } = tankData.fullActiveCert || {};
  const maxVolume = compartment ? roundNoFixed(unit(compartment.maxVolume, 'L').toNumber(volumeUnit), 4) : '';
  
  // Default configuration - all columns visible
  const defaultConfig = {
    showCodeArticleAS400: true,
    showCuverieAS400: true,
    showName: true,
    showRefAlcohol: true,
    showMaxVolume: false,
    showCapacity: false,
    showHeight: true,
    showVolume: true,
    showAbvAtTemperature: true,
    showTemperature: true,
    showAbvAt20C: true,
    showVolumeAt20C: true,
    showAgeInMonth: true,
    showPureAlcohol: true,
    showServiceDate: true,
    showFillPercentage: false,
    showRemainingVolume: false,
    // Default column order
    columnOrder: [
      'showCodeArticleAS400',
      'showCuverieAS400',
      'showName',
      'showRefAlcohol',
      'showMaxVolume',
      'showCapacity',
      'showHeight',
      'showVolume',
      'showAbvAtTemperature',
      'showTemperature',
      'showAbvAt20C',
      'showVolumeAt20C',
      'showAgeInMonth',
      'showPureAlcohol',
      'showServiceDate',
      'showFillPercentage',
      'showRemainingVolume'
    ]
  };

  // Merge default config with provided config
  const finalConfig = { ...defaultConfig, ...config };
  
  // let cellarName = '';
  let height = '';
  let volume = 'VIDE';
  let abvAt20C = '';
  let abvAtTemperature = '';
  let temperature = '';
  let volumeAt20C = '';
  let pureAlcohol = '';
  let ageInMonth = '';
  
  if (operation) {
    // cellarName = operation.cellarName || operation.cellar;
    height = operation.form.results.height?.value || operation.form.results.height;
    const realVolume = operation.form.results.volume?.value || operation.form.results.volume;
    volume = (height === '0' || realVolume === '0' || realVolume === 0) ? 'VIDE' 
      : (height === '0' && realVolume !== '0') ? `0 ↔ ${realVolume}` 
        : realVolume ? parseFloat(realVolume) : '';
    
    // Parse numeric values
    const rawAbvAt20C = operation.form.paConversionResults?.abvAt20c?.value || operation.form.results.volumeConvResult?.abvAt20c?.value || '';
    abvAt20C = rawAbvAt20C ? parseFloat(rawAbvAt20C) : '';

    const rawAbvAtTemperature = operation.form.tavInputs?.measuredTav?.value || '';
    abvAtTemperature = rawAbvAtTemperature ? parseFloat(rawAbvAtTemperature) : '';

    const rawTemperature = operation.form.tavInputs?.tavTemperature?.value || '';
    temperature = rawTemperature ? parseFloat(rawTemperature) : '';

    const rawVolumeAt20C = operation.form.paConversionResults?.volumeAtTemp?.value || operation.form.results.volumeConvResult?.volumeAtTemp?.value || '';
    volumeAt20C = rawVolumeAt20C ? parseFloat(rawVolumeAt20C) : '';
    
    const rawPureAlcohol = operation.form.paConversionResults?.pureAlcoholVolumeAt20c?.value || operation.form.results.volumeConvResult?.pureAlcoholVolumeAt20c?.value || '';
    pureAlcohol = rawPureAlcohol ? parseFloat(rawPureAlcohol) : '';
    
    ageInMonth = operation.form.extraFields?.find(f => f.name === 'age-en-mois')?.value || '';
  } else {
    // const cellarRef = doc(cellarsRef, tankData.cellar);
    // const cellarDoc = await getDoc(cellarRef);
    // cellarName = cellarDoc.exists() ? cellarDoc.data().name : '';
    volume = '';
    height = '';
    abvAt20C = '';
    abvAtTemperature = '';
    temperature = '';
    volumeAt20C = '';
    pureAlcohol = '';
    ageInMonth = '';
  }
  
  // Parse maxVolume to number if it exists
  const parsedMaxVolume = maxVolume ? parseFloat(maxVolume) : '';

  // Calculate the actual row number for this tank in its group
  const actualRow = groupStartRow + index;

  // Build row array based on config
  const row = [];
  
  // Function to get column letter based on enabled columns
  const getColumnLetter = (configKey) => {
    let colIndex = 0;
    for (const key of finalConfig.columnOrder) {
      if (finalConfig[key]) {
        colIndex++;
        if (key === configKey) {
          return String.fromCharCode(64 + colIndex); // A=65 in ASCII
        }
      }
    }
    return null;
  };

  // Map of column keys to their values
  const columnValues = {
    showCodeArticleAS400: tankData.codeArticleAS400,
    showCuverieAS400: tankData.cuverieAS400,
    showName: { t: 's', v: String(tankData.name) },
    showRefAlcohol: tankData.refAlcohol,
    showMaxVolume: parsedMaxVolume,
    showCapacity: tankData.fullActiveCert?.capacity ? parseFloat(tankData.fullActiveCert?.capacity) * 1000 : '',
    showHeight: height,
    showVolume: volume,
    showAbvAtTemperature: abvAtTemperature,
    showTemperature: temperature,
    showAbvAt20C: abvAt20C,
    showVolumeAt20C: volumeAt20C,
    showAgeInMonth: ageInMonth,
    showPureAlcohol: pureAlcohol,
    showServiceDate: tankData.serviceDate
  };

  // Get column letters for formulas
  const volumeCol = getColumnLetter('showVolume');
  const maxVolumeCol = getColumnLetter('showMaxVolume');

  // Add columns in the specified order
  finalConfig.columnOrder.forEach(key => {
    if (finalConfig[key]) {
      if (key === 'showFillPercentage' && volumeCol && maxVolumeCol) {
        row.push({ t: 'n', f: `ROUND((${volumeCol}${actualRow}/${maxVolumeCol}${actualRow})*100, 2)`, v: null, numFmt: '0.00%' });
      } else if (key === 'showRemainingVolume' && volumeCol && maxVolumeCol) {
        row.push({ t: 'n', f: `${maxVolumeCol}${actualRow}-${volumeCol}${actualRow}`, v: null });
      } else if (columnValues[key] !== undefined) {
        row.push(columnValues[key]);
      }
    }
  });

  return {
    cellar: tankData.cellar,
    row
  };
};

export const addGroupSection = (ws, title, startRow, boldCenteredStyle) => {
  // Add empty row
  ws.getRow(startRow);
  
  // Add title row with merged cells
  ws.mergeCells(`D${startRow + 1}:E${startRow + 1}`);
  const titleCell = ws.getCell(`D${startRow + 1}`);
  titleCell.value = title;
  titleCell.style = boldCenteredStyle;
  
  return startRow + 2;
};

// Configuration for different sum formula types
const SUM_FORMULA_CONFIGS = {
  topMature: {
    formulas: [
      // { col: 'E', formula: (start, end, sumRow) => `SUM(E${start}:E${end})` },
      { col: 'F', formula: (start, end, sumRow) => `SUM(F${start}:F${end})` },
      // { col: 'H', formula: (start, end, sumRow) => `SUM(H${start}:H${end})` },
      { col: 'J', formula: (start, end, sumRow) => `SUM(J${start}:J${end})` },
      { col: 'L', formula: (start, end, sumRow) => `SUM(L${start}:L${end})` },
      // { col: 'P', formula: (start, end, sumRow) => `ROUND((H${sumRow}/E${sumRow})*100, 2)` },
      // { col: 'Q', formula: (start, end, sumRow) => `SUM(Q${start}:Q${end})` }
    ]
  },
  stabulationVieux: {
    formulas: [
      // { col: 'E', formula: (start, end, sumRow) => `SUM(E${start}:E${end})` },
      { col: 'G', formula: (start, end, sumRow) => `SUM(G${start}:G${end})` },
      // { col: 'H', formula: (start, end, sumRow) => `SUM(H${start}:H${end})` },
      { col: 'K', formula: (start, end, sumRow) => `SUM(K${start}:K${end})` },
      { col: 'L', formula: (start, end, sumRow) => `SUM(L${start}:L${end})` },
      // { col: 'O', formula: (start, end, sumRow) => `ROUND((H${sumRow}/E${sumRow})*100, 2)` },
      // { col: 'P', formula: (start, end, sumRow) => `SUM(P${start}:P${end})` }
    ]
  },
  pharma: {
    formulas: [
      // { col: 'E', formula: (start, end, sumRow) => `SUM(E${start}:E${end})` },
      { col: 'F', formula: (start, end, sumRow) => `SUM(F${start}:F${end})` },
      // { col: 'H', formula: (start, end, sumRow) => `SUM(H${start}:H${end})` },
      { col: 'K', formula: (start, end, sumRow) => `SUM(K${start}:K${end})` },
      { col: 'J', formula: (start, end, sumRow) => `SUM(J${start}:J${end})` },
      // { col: 'N', formula: (start, end, sumRow) => `ROUND((H${sumRow}/E${sumRow})*100, 2)` },
      // { col: 'O', formula: (start, end, sumRow) => `SUM(O${start}:O${end})` }
    ]
  }
};

// Generic function to generate sum formulas
const generateSumFormulas = (config, startRow, endRow, sumRow, boldStyle) => {
  const sumFormulas = new Array(17).fill(''); // Initialize with empty strings
  sumFormulas[0] = { value: 'Total', style: boldStyle };

  // Add formulas based on configuration
  config.formulas.forEach(({ col, formula }) => {
    const colIndex = col.charCodeAt(0) - 'A'.charCodeAt(0);
    sumFormulas[colIndex] = {
      formula: formula(startRow, endRow, sumRow),
      style: boldStyle
    };
  });

  return sumFormulas;
};

// Refactored functions using the generic generator
export const addTopMatureSumFormulas = (ws, startRow, endRow, sumRow, boldStyle) => {
  const sumFormulas = generateSumFormulas(SUM_FORMULA_CONFIGS.topMature, startRow, endRow, sumRow, boldStyle);
  
  sumFormulas.forEach((value, colIndex) => {
    const cell = ws.getCell(sumRow, colIndex + 1);
    if (typeof value === 'object' && value !== null) {
      if (value.formula) {
        cell.value = { formula: value.formula };
      } else {
        cell.value = value.value;
      }
      cell.style = value.style;
    } else {
      cell.value = value;
    }
  });
};

export const addStabulationVieuxSumFormulas = (ws, startRow, endRow, sumRow, boldStyle) => {
  const sumFormulas = generateSumFormulas(SUM_FORMULA_CONFIGS.stabulationVieux, startRow, endRow, sumRow, boldStyle);
  
  sumFormulas.forEach((value, colIndex) => {
    const cell = ws.getCell(sumRow, colIndex + 1);
    if (typeof value === 'object' && value !== null) {
      if (value.formula) {
        cell.value = { formula: value.formula };
      } else {
        cell.value = value.value;
      }
      cell.style = value.style;
    } else {
      cell.value = value;
    }
  });
};

export const addPharmaSumFormulas = (ws, startRow, endRow, sumRow, boldStyle) => {
  const sumFormulas = generateSumFormulas(SUM_FORMULA_CONFIGS.pharma, startRow, endRow, sumRow, boldStyle);
  
  sumFormulas.forEach((value, colIndex) => {
    const cell = ws.getCell(sumRow, colIndex + 1);
    if (typeof value === 'object' && value !== null) {
      if (value.formula) {
        cell.value = { formula: value.formula };
      } else {
        cell.value = value.value;
      }
      cell.style = value.style;
    } else {
      cell.value = value;
    }
  });
};

